import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Box,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  List,
  ListItemText,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import { useNavigate } from "react-router-dom";
import { apiClient } from "@/services/api";

import CreatePerformanceTargetDialog from '../projects/CreatePerformanceTargetDialog.tsx';
import CreateDepartmentTargetDialog from '../projects/CreateDepartmentTargetDialog.tsx';
import { showSuccessMessage, showErrorMessage } from '@/components/common/GlobalSnackbar.tsx';

// TypeScript 类型定义
interface User {
  id: number;
  username: string;
  role: string;
  org_id: number;
}

interface Project {
  id: number;
  project_name: string;
  project_code: string;
  description?: string;
  user_id: number;
  org_id: number;
  report_template_id?: number;
  industry_type?: string;
  client_level?: string;
  evaluation_type?: string;
  status: "new" | "archived_mentor";
  contract_year?: number;
  dify_dataset_id?: string;
  project_type: string;
  created_at: string;
  updated_at: string;
  report_generation_status?: string;
  report_generation_progress?: number;
  report_generation_error?: string;
  last_generated_at?: string;
  username?: string;
  display_name?: string;  // 创建者显示名称
  organization_name?: string;
  user?: {
    display_name?: string;
    username?: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-tabpanel-${index}`}
      aria-labelledby={`performance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function PerformanceTargetListItem({
  project,
  onDelete,
  onEdit,
  onNavigate,
  loadingStates,
  getUsernameById,
  formatToGMT8,
  currentUser,
}: {
  project: Project;
  onDelete: (project: Project) => void;
  onEdit: (project: Project) => void;
  onNavigate: (projectId: number) => void;
  loadingStates: any;
  getUsernameById: (userId: number) => string;
  formatToGMT8: (dateStr: string | null) => string;
  currentUser: User | null;
}) {
  // 检查项目是否正在自动处理中
  const isProcessing = project.report_generation_status === "generating";
  // 检查项目是否已完成报告生成
  const isCompleted = project.report_generation_status === "completed";

  return (
    <React.Fragment>
      <div
        onClick={() => {
          onNavigate(project.id);
        }}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "12px 0",
          cursor: "pointer",
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <span>{`${project.project_code}-${project.project_name}`}</span>
              {isProcessing && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "warning.main",
                    color: "warning.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    boxShadow: 1
                  }}
                >
                  <CircularProgress size={14} sx={{ color: "warning.contrastText" }} />
                  <span>自动生成指标中</span>
                  <Box sx={{
                    backgroundColor: "rgba(255,255,255,0.3)",
                    px: 0.5,
                    borderRadius: 0.5,
                    fontSize: "0.75rem"
                  }}>
                    {project.report_generation_progress || 0}%
                  </Box>
                </Box>
              )}
              {isCompleted && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "success.main",
                    color: "success.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    boxShadow: 1
                  }}
                >
                  <span>报告生成已完成</span>
                  {project.last_generated_at && (
                    <Box sx={{
                      backgroundColor: "rgba(255,255,255,0.3)",
                      px: 0.5,
                      borderRadius: 0.5,
                      fontSize: "0.75rem"
                    }}>
                      {formatToGMT8(project.last_generated_at)}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          }
          secondary={
            <React.Fragment>
              <Box
                sx={{
                  display: "block",
                  mt: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  创建时间: {formatToGMT8(project.created_at)} | 
                  创建者: {
                    project.display_name ||
                    project.username ||
                    project.user?.display_name ||
                    project.user?.username ||
                    getUsernameById(project.user_id)
                  }
                </Typography>
              </Box>
            </React.Fragment>
          }
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="编辑">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(project);
              }}
              disabled={loadingStates.editing && loadingStates.projectId === project.id}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          {(currentUser?.role === "super_admin" || currentUser?.role === "zhaoyuan_admin") && (
            <Tooltip title="删除">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(project);
                }}
                color="error"
                disabled={loadingStates.deleting && loadingStates.projectId === project.id}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </div>
    </React.Fragment>
  );
}

interface PerformanceTargetFormProps {
  // 可以添加props用于接收外部数据或回调
}

const PerformanceTargetForm: React.FC<PerformanceTargetFormProps> = () => {
  const navigate = useNavigate();
  
  // 状态管理
  const [projectTargets, setProjectTargets] = useState<Project[]>([]); // 具体项目目标
  const [departmentTargets, setDepartmentTargets] = useState<Project[]>([]); // 部门整体目标
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // 页签状态
  const [tabValue, setTabValue] = useState(0); // 0: 具体项目目标, 1: 部门整体目标
  
  // 过滤状态
  const [projectFilterText, setProjectFilterText] = useState("");
  const [departmentFilterText, setDepartmentFilterText] = useState("");
  
  // 对话框状态
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [createDepartmentDialogOpen, setCreateDepartmentDialogOpen] = useState(false);
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [editDepartmentDialogOpen, setEditDepartmentDialogOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [departmentToEdit, setDepartmentToEdit] = useState<Project | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  
  // 加载状态
  const [loadingAction, setLoadingAction] = useState({
    editing: false,
    deleting: false,
    projectId: null as number | null,
  });
  
  // 获取当前用户
  useEffect(() => {
    const userStr = localStorage.getItem("currentUser");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (e) {
      }
    }
  }, []);

  // 获取用户名
  const getUsernameById = useCallback((userId: number): string => {
    const allProjects = [...projectTargets, ...departmentTargets];
    const project = allProjects.find(p => p.user_id === userId);
    return project?.username || project?.display_name || `用户${userId}`;
  }, [projectTargets, departmentTargets]);

  // 格式化日期（后端返回的时间已加8小时，但时区标记是+00:00，需要修正）
  const formatToGMT8 = useCallback((dateStr: string | null): string => {
    if (!dateStr) return "未知";
    try {
      // 如果后端返回的时间时区标记是+00:00，但实际已经是北京时间，需要替换为+08:00
      let normalized = dateStr.replace(" ", "T");
      // 将 +00:00 替换为 +08:00，因为后端返回的时间已经是北京时间
      if (normalized.includes("+00:00")) {
        normalized = normalized.replace("+00:00", "+08:00");
      }
      const date = new Date(normalized);
      if (isNaN(date.getTime())) return dateStr;
      // 直接格式化，不进行时区转换（因为时间已经是正确的北京时间）
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      return `${year}/${month}/${day} ${hour}:${minute}`;
    } catch {
      return dateStr;
    }
  }, []);

  // 获取具体项目目标列表
  const fetchProjectTargets = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get('/projects/', {
        page: 1,
        page_size: 100,
        project_type: "performance_target",
      });
      
      
      if (response.success && response.data) {
        setProjectTargets(response.data);
        setError(null);
      } else {
        setError(response.message || "获取具体项目目标列表失败");
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "获取具体项目目标列表失败");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // 获取部门整体目标列表
  const fetchDepartmentTargets = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get('/projects/', {
        page: 1,
        page_size: 100,
        project_type: "department_overall",
      });
      
      
      if (response.success && response.data) {
        setDepartmentTargets(response.data);
        setError(null);
      } else {
        setError(response.message || "获取部门整体目标列表失败");
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "获取部门整体目标列表失败");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // 初始加载
  useEffect(() => {
    fetchProjectTargets();
    fetchDepartmentTargets();
  }, [fetchProjectTargets, fetchDepartmentTargets]);

  // 处理项目点击
  const handleNavigate = useCallback((projectId: number) => {
    // 所有项目都跳转到探索界面
    navigate(`/projects/${projectId}`);
  }, [navigate]);

  // 处理编辑项目（打开编辑对话框而不是跳转）
  const handleEditProject = useCallback((project: Project) => {
    if (project.project_type === 'performance_target') {
      setProjectToEdit(project);
      setEditProjectDialogOpen(true);
    } else if (project.project_type === 'department_overall') {
      setDepartmentToEdit(project);
      setEditDepartmentDialogOpen(true);
    }
  }, []);

  // 处理删除项目
  const handleDeleteProject = useCallback((project: Project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  }, []);

  // 确认删除项目
  const confirmDeleteProject = useCallback(async () => {
    if (!projectToDelete) return;
    
    setLoadingAction({ editing: false, deleting: true, projectId: projectToDelete.id });
    
    try {
      await apiClient.delete(`/projects/${projectToDelete.id}`);
      showSuccessMessage("项目删除成功");
      
      // 根据项目类型刷新对应的列表
      if (projectToDelete.project_type === 'performance_target') {
        fetchProjectTargets();
      } else if (projectToDelete.project_type === 'department_overall') {
        fetchDepartmentTargets();
      }
    } catch (err: any) {
      showErrorMessage(err.response?.data?.detail || "删除项目失败");
    } finally {
      setLoadingAction({ editing: false, deleting: false, projectId: null });
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  }, [projectToDelete, fetchProjectTargets, fetchDepartmentTargets]);

  // 处理创建具体项目目标
  const handleCreateProject = useCallback(() => {
    setCreateProjectDialogOpen(true);
  }, []);

  // 处理创建部门整体目标
  const handleCreateDepartment = useCallback(() => {
    setCreateDepartmentDialogOpen(true);
  }, []);

  // 处理具体项目目标创建/编辑成功
  const handleProjectCreated = useCallback(() => {
    setCreateProjectDialogOpen(false);
    setEditProjectDialogOpen(false);
    setProjectToEdit(null);
    fetchProjectTargets();
  }, [fetchProjectTargets]);

  // 处理部门整体目标创建/编辑成功
  const handleDepartmentCreated = useCallback(() => {
    setCreateDepartmentDialogOpen(false);
    setEditDepartmentDialogOpen(false);
    setDepartmentToEdit(null);
    fetchDepartmentTargets();
  }, [fetchDepartmentTargets]);

  // 计算统计数据
  const projectStats = useMemo(() => {
    const totalInProgress = projectTargets.filter(p => p.status === "new").length;
    const totalArchived = projectTargets.filter(p => p.status === "archived_mentor").length;
    const totalGenerating = projectTargets.filter(p => p.report_generation_status === "generating").length;
    
    return {
      totalInProgress,
      totalArchived,
      totalGenerating,
      total: projectTargets.length
    };
  }, [projectTargets]);

  const departmentStats = useMemo(() => {
    const totalInProgress = departmentTargets.filter(p => p.status === "new").length;
    const totalArchived = departmentTargets.filter(p => p.status === "archived_mentor").length;
    const totalGenerating = departmentTargets.filter(p => p.report_generation_status === "generating").length;
    
    return {
      totalInProgress,
      totalArchived,
      totalGenerating,
      total: departmentTargets.length
    };
  }, [departmentTargets]);

  // 过滤项目
  const filteredProjectTargets = useMemo(() => {
    let filtered = projectTargets;
    
    if (projectFilterText) {
      filtered = filtered.filter(project =>
        project.project_name.toLowerCase().includes(projectFilterText.toLowerCase()) ||
        project.project_code.toLowerCase().includes(projectFilterText.toLowerCase())
      );
    }
    
    return filtered;
  }, [projectTargets, projectFilterText]);

  const filteredDepartmentTargets = useMemo(() => {
    let filtered = departmentTargets;
    
    if (departmentFilterText) {
      filtered = filtered.filter(project =>
        project.project_name.toLowerCase().includes(departmentFilterText.toLowerCase()) ||
        project.project_code.toLowerCase().includes(departmentFilterText.toLowerCase())
      );
    }
    
    return filtered;
  }, [departmentTargets, departmentFilterText]);

  // 按状态分组项目
  const inProgressProjectTargets = useMemo(() => 
    filteredProjectTargets.filter(p => p.status === "new"), 
    [filteredProjectTargets]
  );
  
  const inProgressDepartmentTargets = useMemo(() => 
    filteredDepartmentTargets.filter(p => p.status === "new"), 
    [filteredDepartmentTargets]
  );

  if (!currentUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const commonListProps = {
    onDelete: handleDeleteProject,
    onEdit: handleEditProject,
    onNavigate: handleNavigate,
    loadingStates: loadingAction,
    getUsernameById,
    formatToGMT8,
    currentUser,
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mt: 1, background: 'transparent' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} aria-label="performance target tabs">
          <Tab label={`具体项目目标 (${projectStats.total})`} id="performance-tab-0" />
          <Tab label={`部门整体目标 (${departmentStats.total})`} id="performance-tab-1" />
        </Tabs>
      </Box>

      {/* 具体项目目标页签 */}
      <TabPanel value={tabValue} index={0}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="搜索具体项目目标..."
              value={projectFilterText}
              onChange={(e) => setProjectFilterText(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleCreateProject}
          >
            创建新项目
          </Button>
        </Box>
        <List>
          {inProgressProjectTargets.length > 0 ? (
            inProgressProjectTargets.map((p) => (
              <PerformanceTargetListItem key={p.id} project={p} {...commonListProps} />
            ))
          ) : (
            <Alert severity="info">您当前没有正在编写的具体项目目标。</Alert>
          )}
        </List>
      </TabPanel>

      {/* 部门整体目标页签 */}
      <TabPanel value={tabValue} index={1}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="搜索部门整体目标..."
              value={departmentFilterText}
              onChange={(e) => setDepartmentFilterText(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleCreateDepartment}
          >
            创建新项目
          </Button>
        </Box>
        <List>
          {inProgressDepartmentTargets.length > 0 ? (
            inProgressDepartmentTargets.map((p) => (
              <PerformanceTargetListItem key={p.id} project={p} {...commonListProps} />
            ))
          ) : (
            <Alert severity="info">您当前没有正在编写的部门整体目标。</Alert>
          )}
        </List>
      </TabPanel>

      {/* 创建具体项目目标对话框 */}
      <CreatePerformanceTargetDialog
        open={createProjectDialogOpen}
        onClose={() => setCreateProjectDialogOpen(false)}
        onProjectCreated={handleProjectCreated}
      />

      {/* 编辑具体项目目标对话框 */}
      <CreatePerformanceTargetDialog
        open={editProjectDialogOpen}
        onClose={() => { setEditProjectDialogOpen(false); setProjectToEdit(null); }}
        onProjectCreated={handleProjectCreated}
        editMode
        projectDataToEdit={projectToEdit}
      />

      {/* 创建部门整体目标对话框 */}
      <CreateDepartmentTargetDialog
        open={createDepartmentDialogOpen}
        onClose={() => setCreateDepartmentDialogOpen(false)}
        onProjectCreated={handleDepartmentCreated}
      />

      {/* 编辑部门整体目标对话框 */}
      <CreateDepartmentTargetDialog
        open={editDepartmentDialogOpen}
        onClose={() => { setEditDepartmentDialogOpen(false); setDepartmentToEdit(null); }}
        onProjectCreated={handleDepartmentCreated}
        editMode
        projectDataToEdit={departmentToEdit}
      />

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除项目 "{projectToDelete?.project_name}" 吗？此操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={confirmDeleteProject} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PerformanceTargetForm;